// Shared helpers: category labels, cost normalization, currency and date formatting.

export const CATEGORIES = [
  { value: "streaming", label: "Streaming" },
  { value: "musique", label: "Musique" },
  { value: "telephonie", label: "Téléphonie" },
  { value: "sport", label: "Sport" },
  { value: "logiciels", label: "Logiciels" },
  { value: "jeux", label: "Jeux vidéo" },
  { value: "presse", label: "Presse" },
  { value: "autre", label: "Autre" },
];

export const FREQUENCIES = [
  { value: "hebdomadaire", label: "Hebdomadaire" },
  { value: "mensuelle", label: "Mensuelle" },
  { value: "trimestrielle", label: "Trimestrielle" },
  { value: "annuelle", label: "Annuelle" },
];

export const STATUSES = [
  { value: "actif", label: "Actif" },
  { value: "resilie", label: "Résilié" },
];

// Convert a payment to monthly equivalent (euros/month)
export function toMonthly(price, frequency) {
  const p = Number(price) || 0;
  switch (frequency) {
    case "hebdomadaire":
      return p * 52 / 12;
    case "mensuelle":
      return p;
    case "trimestrielle":
      return p / 3;
    case "annuelle":
      return p / 12;
    default:
      return p;
  }
}

export function toYearly(price, frequency) {
  return toMonthly(price, frequency) * 12;
}

export function formatEUR(value) {
  const num = Number.isFinite(value) ? value : 0;
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

export function formatDateFR(isoDate) {
  if (!isoDate) return "—";
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(d);
}

export function daysUntil(isoDate) {
  if (!isoDate) return null;
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  const diffMs = d.getTime() - today.getTime();
  return Math.round(diffMs / (1000 * 60 * 60 * 24));
}

export function categoryLabel(value) {
  return CATEGORIES.find((c) => c.value === value)?.label || value;
}

export function frequencyLabel(value) {
  return FREQUENCIES.find((c) => c.value === value)?.label || value;
}

// Aggregate active subscriptions into category totals (monthly)
export function aggregateByCategory(subs) {
  const map = {};
  subs
    .filter((s) => s.status === "actif")
    .forEach((s) => {
      const monthly = toMonthly(s.price, s.frequency);
      map[s.category] = (map[s.category] || 0) + monthly;
    });
  return Object.entries(map).map(([category, monthly]) => ({
    category,
    label: categoryLabel(category),
    monthly,
  }));
}

// Build recommendations deterministically from data only.
// Each recommendation includes: id, type, title, description, currentMonthly, alternativeMonthly, savingsMonthly, condition
export function buildRecommendations(subs) {
  const active = subs.filter((s) => s.status === "actif");
  const recs = [];

  // 1. Duplicate categories (multiple services in same category)
  const byCategory = {};
  active.forEach((s) => {
    byCategory[s.category] = byCategory[s.category] || [];
    byCategory[s.category].push(s);
  });
  Object.entries(byCategory).forEach(([cat, items]) => {
    if (items.length >= 2 && ["streaming", "musique", "presse"].includes(cat)) {
      const sorted = [...items].sort(
        (a, b) => toMonthly(b.price, b.frequency) - toMonthly(a.price, a.frequency)
      );
      const removed = sorted[0];
      const currentMonthly = items.reduce(
        (sum, s) => sum + toMonthly(s.price, s.frequency),
        0
      );
      const alternativeMonthly =
        currentMonthly - toMonthly(removed.price, removed.frequency);
      recs.push({
        id: `dup-${cat}`,
        type: "doublon",
        priority: 1,
        title: `Doublon de catégorie : ${categoryLabel(cat)}`,
        description: `Vous avez ${items.length} services de ${categoryLabel(
          cat
        )}. Envisagez de ne conserver que ceux que vous utilisez vraiment. Exemple : résilier ${removed.name} vous ferait économiser.`,
        currentMonthly,
        alternativeMonthly,
        savingsMonthly: currentMonthly - alternativeMonthly,
        condition:
          "À vérifier : estimez votre usage réel avant de résilier. Certaines résiliations peuvent avoir un préavis.",
        subscriptionIds: items.map((i) => i.id),
      });
    }
  });

  // 2. Expensive subscriptions (monthly > 20€)
  active.forEach((s) => {
    const monthly = toMonthly(s.price, s.frequency);
    if (monthly >= 20) {
      recs.push({
        id: `expensive-${s.id}`,
        type: "cher",
        priority: 2,
        title: `${s.name} coûte ${formatEUR(monthly)} / mois`,
        description: `Cet abonnement est l'un de vos plus coûteux. Comparez avec les offres actuelles du marché ou envisagez une formule inférieure.`,
        currentMonthly: monthly,
        alternativeMonthly: null,
        savingsMonthly: null,
        condition:
          "Prix alternatif à renseigner manuellement dans le simulateur. Vérifiez les offres avant de changer.",
        subscriptionIds: [s.id],
      });
    }
  });

  // 3. Annual plans — potential savings if switched from monthly (informative only)
  active.forEach((s) => {
    if (s.frequency === "mensuelle" && toMonthly(s.price, s.frequency) >= 8) {
      recs.push({
        id: `annual-${s.id}`,
        type: "fréquence",
        priority: 3,
        title: `${s.name} : vérifier l'offre annuelle`,
        description: `Certains services offrent une remise de ~15 % sur un paiement annuel. À confirmer directement auprès du service.`,
        currentMonthly: toMonthly(s.price, s.frequency),
        alternativeMonthly: null,
        savingsMonthly: null,
        condition:
          "Information à vérifier : la remise annuelle dépend du fournisseur. Renseignez le prix annuel réel dans le simulateur pour chiffrer l'économie.",
        subscriptionIds: [s.id],
      });
    }
  });

  // 4. Underused reminder (we cannot know usage — we only ask)
  active.forEach((s) => {
    const monthly = toMonthly(s.price, s.frequency);
    if (monthly >= 10) {
      recs.push({
        id: `usage-${s.id}`,
        type: "usage",
        priority: 4,
        title: `Utilisez-vous encore ${s.name} ?`,
        description: `Un check rapide : si vous ne l'avez pas utilisé ce dernier mois, envisagez une pause ou une résiliation.`,
        currentMonthly: monthly,
        alternativeMonthly: 0,
        savingsMonthly: monthly,
        condition:
          "Nous ne pouvons pas vérifier automatiquement votre usage. Décision à prendre après évaluation personnelle.",
        subscriptionIds: [s.id],
      });
    }
  });

  return recs.sort((a, b) => a.priority - b.priority);
}
